import clsx from 'clsx';
import React from 'react';
import type {VNode} from 'snabbdom';

import type {SnabbdomElementProps, SnabbdomOrReactNode} from './types';
import {LegacyPanelRenderer} from './renderers/panel';
import {SnabbdomRenderer} from './renderers/snabbdom';
import {getStandardAttributes} from './renderers/util';

/**
 * The vnode's selector, or `undefined` for anything that is not a snabbdom vnode — a React
 * element, a primitive, an array. Keying and tag lookup both hinge on it.
 */
function snabbdomSel(node: SnabbdomOrReactNode): string | undefined {
  if (!node || typeof node !== `object` || Array.isArray(node) || React.isValidElement(node)) {
    return undefined;
  }
  return (node as VNode).sel;
}

/**
 * Assigns React keys to sibling VNodes for stable reconciliation.
 *
 * Snabbdom's JSX filters out falsy children (null/false/undefined), which causes
 * siblings to shift positions between renders. Without keys, React reconciles by
 * position and will remount siblings that shifted. Using sel as the key mirrors
 * snabbdom's own matching behavior (sel + key).
 *
 * For duplicate selectors among siblings, appends an index suffix for uniqueness.
 */
function withKeys(children: SnabbdomOrReactNode[]): React.ReactNode[] {
  const selCounts = new Map<string, number>();
  for (const child of children) {
    const sel = snabbdomSel(child);
    if (sel) {
      selCounts.set(sel, (selCounts.get(sel) || 0) + 1);
    }
  }

  const selIndices = new Map<string, number>();
  return children.map((child, index) => {
    const node = snabbdomToReact(child);

    if (!React.isValidElement(node)) {
      return node;
    }

    const sel = snabbdomSel(child);

    // A React element has no `sel` to key by. React would fall back to reconciling it by position
    // anyway, so a positional key preserves that behavior without the missing-key warning.
    if (!sel) {
      return node.key === null || node.key === undefined ? React.cloneElement(node, {key: `react__${index}`}) : node;
    }

    // explicit snabbdom key always wins
    const {key} = child as VNode;
    if (key !== null && key !== undefined) {
      return React.cloneElement(node, {key: String(key)});
    }

    const count = selCounts.get(sel)!;

    if (count > 1) {
      const idx = selIndices.get(sel) || 0;
      selIndices.set(sel, idx + 1);
      return React.cloneElement(node, {key: `${sel}__${idx}`});
    }

    return React.cloneElement(node, {key: sel});
  });
}

/**
 * Transforms a Snabbdom VNode tree into a React node tree.
 */
export function snabbdomToReact(node: SnabbdomOrReactNode): React.ReactNode {
  // snabbdom drops falsey children, so a renderer returning `null` for a cell it has nothing to
  // put in is an ordinary shape here rather than a mistake.
  if (node === null || node === undefined || typeof node === `boolean`) {
    return null;
  }

  // A renderer authored in a React file returns a React element rather than a vnode. React renders
  // it as-is, so a callsite can move its renderers over without waiting on the component it
  // renders into, and vice versa.
  if (React.isValidElement(node)) {
    return node;
  }

  // arrays, of either form
  if (Array.isArray(node)) {
    return withKeys(node);
  }

  // plain text nodes
  if (typeof node === `string` || typeof node === `number`) {
    return node;
  }

  const vnode = node as VNode;

  if (typeof vnode.text === `string` && !vnode.sel) {
    return vnode.text;
  }

  // A fragment (`<>...</>`) has no `sel`, so there is no tag to render it as. snabbdom-jsx-lite
  // collapses a fragment whose only child is text into a `text` vnode, which the branch above
  // returns; one wrapping elements keeps them in `children`, and React renders those directly.
  if (!vnode.sel) {
    return vnode.children ? <>{withKeys(vnode.children)}</> : null;
  }

  const selParts: string[] = vnode.sel.split(`.`);
  const className = clsx(
    // classes in selector
    selParts?.slice(1).join(` `),
    // `class` prop
    vnode.data?.class,
  );

  const attrs: SnabbdomElementProps[`attrs`] = vnode.data?.attrs || {};

  if (className) {
    attrs.className = className;
  }

  if (vnode.data?.style) {
    attrs.style = vnode.data.style;
  }

  // apply data- attributes
  if (vnode.data?.dataset) {
    for (const [key, value] of Object.entries(vnode.data.dataset)) {
      attrs[`data-${key}`] = value;
    }
  }

  const children = vnode.children ? withKeys(vnode.children) : vnode.text || undefined;

  const snabbdomProps: SnabbdomElementProps = {
    tagName: selParts[0],
    ...(vnode.data?.on ? {on: vnode.data.on as any} : {}),
    ...(vnode.data?.hook ? {hooks: vnode.data.hook as any} : {}),
    ...(typeof vnode.data?.props?.innerHTML === `string` ? {innerHTML: vnode.data.props.innerHTML} : {}),
    ...(children !== null ? {children} : {}),
  };

  // wrap in panel handler if it's a real Panel component
  if (vnode.data?.panel) {
    return (
      <LegacyPanelRenderer {...snabbdomProps} attrs={attrs} params={vnode.data?.params} props={vnode.data?.props} />
    );
  }

  return <SnabbdomRenderer {...snabbdomProps} attrs={getStandardAttributes(attrs)} props={vnode.data?.props} />;
}
